import { DecoratorNode, $applyNodeReplacement, createCommand } from 'lexical';
import * as React from 'react';
import { isAllowedMediaUrl } from '../../../utils/docMediaHosts';
import {
  DOC_MEDIA_ENC_ALG, DOC_MEDIA_ENC_KEY_RE, DOC_MEDIA_PENDING_REF_RE,
  DOC_MEDIA_MIME_RE, DOC_MEDIA_DIM_RE, DOC_MEDIA_SIZE_RE, sanitizeDocMediaName,
} from '../../../utils/sanitizeHtml';
import DocImageComponent from './DocImageComponent';

/**
 * An encrypted image block in a document.
 *
 * The bucket stores AES-256-GCM CIPHERTEXT (`.bin`, public-read, unguessable
 * URL); the PER-ATTACHMENT key `k` (base64 32 bytes) travels inside this
 * node's JSON, so whoever can read the document can decrypt the image and
 * nobody else can — the wire format is chat's exact one (12-byte IV prepended,
 * utils/chatCrypto.js). Rendering fetches the ciphertext with the webview's
 * fetch and decrypts through the blob-URL LRU (utils/decryptedMediaCache.js).
 *
 * INVARIANT — pinned by docImageNode.test.js: the serialized node NEVER
 * contains image bytes. `src` is an allowlisted https URL or empty; a data:
 * URL never enters this node (importDOM refuses to claim data: imgs, importJSON
 * refuses non-allowlisted srcs). Inline base64 images in the document body
 * would multiply the autosave payload by the image size on every keystroke.
 *
 * Two shapes, decided by which of src/pendingRef is set:
 *  - UPLOADED: `src` set, `pendingRef` empty — normal render path.
 *  - PENDING:  `src` empty, `pendingRef` set — inserted while the upload is
 *    still in flight (services/documentAttachmentService). While THIS app
 *    session staged it, DocImageComponent shows the local preview + upload
 *    ribbon; after a restart the registry is gone (persistent outbox is a
 *    later chunk) and it renders as the reserved placeholder box.
 *
 * Saved-HTML shape (exportDOM writes; sanitizeHtml.js sanitizeImg validates
 * with the SAME imported patterns; importDOM claims):
 *   uploaded: <img src data-enc-key data-enc data-mime data-w data-h
 *                  data-name data-size alt>
 *   pending:  same minus src, plus data-pending-ref
 * Plain <img> (data: included) is NOT claimed — those keep today's behavior
 * (survive sanitize as legacy pasted images, unclaimed by any Lexical node).
 *
 * REGISTERED BEFORE ANY INSERTION UI EXISTS, deliberately: a client that
 * cannot parse this node type drops the block from the parsed state, and its
 * next LWW autosave writes the document back WITHOUT it — silent data loss.
 * So registration must be on every install a release before anything can
 * create these blocks. Do not add paste handlers/commands to this file.
 */

/** Payload: a File to stage directly, or undefined to open the image picker.
 *  Registered ONLY by components/ui/DocMediaPlugin.js (media-enabled hosts);
 *  dispatched by the slash menu's Image item. Lives here, not in the plugin,
 *  so hosts import commands from the node module (playground convention). */
export const INSERT_DOC_IMAGE_COMMAND = createCommand('INSERT_DOC_IMAGE_COMMAND');

/** '123'-style attr -> positive int, else null. */
function parseDim(value) {
  if (!DOC_MEDIA_DIM_RE.test(value || '')) return null;
  const n = parseInt(value, 10);
  return n > 0 ? n : null;
}

function $convertDocImageElement(domNode) {
  const src = domNode.getAttribute('src') || '';
  const k = domNode.getAttribute('data-enc-key') || '';
  const enc = domNode.getAttribute('data-enc') || '';
  const pendingRef = domNode.getAttribute('data-pending-ref') || '';
  if (!DOC_MEDIA_ENC_KEY_RE.test(k) || enc !== DOC_MEDIA_ENC_ALG) return null;
  const uploaded = src !== '' && isAllowedMediaUrl(src);
  const pending = src === '' && DOC_MEDIA_PENDING_REF_RE.test(pendingRef);
  if (!uploaded && !pending) return null;

  const w = parseDim(domNode.getAttribute('data-w'));
  const h = parseDim(domNode.getAttribute('data-h'));
  const sizeAttr = domNode.getAttribute('data-size') || '';
  const mimeAttr = domNode.getAttribute('data-mime') || '';
  const node = $createDocImageNode({
    src: uploaded ? src : '',
    pendingRef: pending ? pendingRef : '',
    k,
    name: domNode.getAttribute('data-name') || '', // constructor sanitizes
    // Read back, or a resize would survive exactly until the document was
    // saved as HTML and parsed again.
    width: Number(domNode.getAttribute('data-width')) || null,
    mime: mimeAttr, // constructor validates
    size: DOC_MEDIA_SIZE_RE.test(sizeAttr) ? parseInt(sizeAttr, 10) : 0,
    w: w != null && h != null ? w : null,
    h: w != null && h != null ? h : null,
    alt: domNode.getAttribute('alt') || '',
  });
  return { node };
}

export class DocImageNode extends DecoratorNode {
  __src; // allowlisted https URL of the ciphertext, or '' while pending
  __pendingRef; // outbox ref while uploading, or ''
  __k; // per-attachment key, base64 32 bytes
  __name; // original filename (display only, sanitized)
  __mime; // decrypted mime, e.g. image/png
  __size; // plaintext byte size (display only)
  __w; // intrinsic px, both-or-neither with __h (reserves the aspect box)
  __h;
  __alt;

  static getType() {
    return 'doc-image';
  }

  static clone(node) {
    return new DocImageNode(
      node.__src, node.__pendingRef, node.__k, node.__name, node.__mime,
      node.__size, node.__w, node.__h, node.__alt, node.__width, node.__key
    );
  }

  constructor(src, pendingRef, k, name, mime, size, w, h, alt, width, key) {
    super(key);
    // Validation lives HERE, not only in the importers: every way a
    // DocImageNode can come to exist (importDOM, importJSON, clone, a future
    // insertion command) funnels through this constructor, so the no-bytes /
    // allowlisted-src invariant is structural — a data: or foreign-host src
    // coerces to '', a malformed key/ref to ''.
    this.__src = typeof src === 'string' && src !== '' && isAllowedMediaUrl(src) ? src : '';
    this.__pendingRef =
      typeof pendingRef === 'string' && DOC_MEDIA_PENDING_REF_RE.test(pendingRef) ? pendingRef : '';
    this.__k = typeof k === 'string' && DOC_MEDIA_ENC_KEY_RE.test(k) ? k : '';
    this.__name = sanitizeDocMediaName(name);
    this.__mime = typeof mime === 'string' && DOC_MEDIA_MIME_RE.test(mime) ? mime : '';
    // Bounds mirror the sanitizer's attr patterns (data-size <= 9 digits,
    // data-w/h <= 5) so live node state never exceeds what its own saved
    // HTML round-trips — sanitize of our exportDOM output stays a no-op.
    this.__size = Number.isInteger(size) && size > 0 && size <= 999_999_999 ? size : 0;
    const wOk = Number.isInteger(w) && w > 0 && w <= 99_999;
    const hOk = Number.isInteger(h) && h > 0 && h <= 99_999;
    this.__w = wOk && hOk ? w : null;
    this.__h = wOk && hOk ? h : null;
    this.__alt = alt || '';
    // DISPLAY width in px, chosen by the user's resize drag. Deliberately
    // separate from __w/__h, which are the image's NATURAL dimensions from the
    // upload probe: those give the box its aspect ratio and must survive a
    // resize, or a second resize would compound against the first and the
    // image would drift out of proportion.
    // Same bound as __w so a resized image's saved HTML still round-trips
    // through the sanitizer's 5-digit data-width pattern unchanged.
    this.__width = Number.isInteger(width) && width > 0 && width <= 99_999 ? width : null;
  }

  /** Set the display width (px), or null to return to natural size. */
  setWidth(width) {
    const writable = this.getWritable();
    writable.__width = Number.isInteger(width) && width > 0 && width <= 99_999 ? width : null;
    return writable;
  }

  static importJSON(serializedNode) {
    // The constructor validates everything (see there) — a synced document
    // whose JSON was hand-mangled degrades field-by-field instead of ever
    // holding a foreign src or malformed key.
    return $createDocImageNode({
      src: serializedNode.src,
      pendingRef: serializedNode.pendingRef,
      k: serializedNode.k,
      name: serializedNode.name,
      mime: serializedNode.mime,
      size: serializedNode.size,
      w: serializedNode.w,
      h: serializedNode.h,
      alt: typeof serializedNode.alt === 'string' ? serializedNode.alt : '',
      width: serializedNode.width,
    });
  }

  exportJSON() {
    return {
      ...super.exportJSON(),
      type: 'doc-image',
      version: 1,
      src: this.__src,
      pendingRef: this.__pendingRef,
      k: this.__k,
      enc: DOC_MEDIA_ENC_ALG,
      name: this.__name,
      mime: this.__mime,
      size: this.__size,
      w: this.__w,
      h: this.__h,
      alt: this.__alt,
      width: this.__width,
    };
  }

  static importDOM() {
    return {
      img: (domNode) => {
        if (!domNode.getAttribute) return null;
        const encKey = domNode.getAttribute('data-enc-key') || '';
        const src = domNode.getAttribute('src') || '';
        const pendingRef = domNode.getAttribute('data-pending-ref') || '';
        // Claim ONLY our two shapes. A plain/pasted <img> — data: URLs
        // included — must stay unclaimed exactly as before this node existed,
        // and a foreign-host src must never be claimed even when an enc key
        // rides along (the conversion re-validates the rest).
        const claims =
          (encKey !== '' && (src === '' || isAllowedMediaUrl(src))) ||
          DOC_MEDIA_PENDING_REF_RE.test(pendingRef);
        if (!claims) return null;
        return { conversion: $convertDocImageElement, priority: 2 };
      },
    };
  }

  exportDOM() {
    const element = document.createElement('img');
    if (this.__src) {
      element.setAttribute('src', this.__src);
    } else if (this.__pendingRef) {
      element.setAttribute('data-pending-ref', this.__pendingRef);
    }
    element.setAttribute('data-enc-key', this.__k);
    element.setAttribute('data-enc', DOC_MEDIA_ENC_ALG);
    if (this.__mime) element.setAttribute('data-mime', this.__mime);
    if (this.__w != null && this.__h != null) {
      element.setAttribute('data-w', String(this.__w));
      element.setAttribute('data-h', String(this.__h));
    }
    if (this.__width != null) element.setAttribute('data-width', String(this.__width));
    if (this.__name) element.setAttribute('data-name', this.__name);
    if (this.__size) element.setAttribute('data-size', String(this.__size));
    if (this.__alt) element.setAttribute('alt', this.__alt);
    return { element };
  }

  isInline() {
    return false;
  }

  createDOM(config) {
    const span = document.createElement('span');
    const className = config.theme.docImage;
    if (className) span.className = className;
    return span;
  }

  updateDOM() {
    return false;
  }

  getTextContent() {
    return this.__alt || '';
  }

  getSrc() {
    return this.getLatest().__src;
  }

  getPendingRef() {
    return this.getLatest().__pendingRef;
  }

  getKeyB64() {
    return this.getLatest().__k;
  }

  /**
   * The pending -> uploaded transition (DocMediaPlugin's live rewriter): sets
   * the ciphertext URL and clears the pendingRef in ONE write, funneled
   * through the same allowlist the constructor enforces — a non-allowlisted
   * url is refused wholesale (the node stays pending) rather than
   * half-applied. Returns whether the flip happened.
   */
  setUploaded(src) {
    if (typeof src !== 'string' || src === '' || !isAllowedMediaUrl(src)) return false;
    const writable = this.getWritable();
    writable.__src = src;
    writable.__pendingRef = '';
    return true;
  }

  decorate() {
    return (
      <DocImageComponent
        nodeKey={this.__key}
        src={this.__src}
        pendingRef={this.__pendingRef}
        k={this.__k}
        mime={this.__mime}
        name={this.__name}
        w={this.__w}
        h={this.__h}
        alt={this.__alt}
        width={this.__width}
      />
    );
  }
}

export function $createDocImageNode({
  src = '', pendingRef = '', k = '', name = '', mime = '', size = 0, w = null, h = null, alt = '',
  width = null,
} = {}) {
  return $applyNodeReplacement(
    new DocImageNode(src, pendingRef, k, name, mime, size, w, h, alt, width)
  );
}

export function $isDocImageNode(node) {
  return node instanceof DocImageNode;
}
